/**
 * Utility functions for handling breed images.
 *
 * Kept inside the Petso app repo (no dependency on `website/`).
 */

// NOTE:
// These curated URLs intentionally mirror the marketing website’s “Find Pet” cards
// (see `website/src/app/utils/breed-images.ts`) so the app and website show the same imagery.

const breedImageMap: Record<string, string> = {
  // ===== DOGS =====
  'golden-retriever':
    'https://images.unsplash.com/photo-1637852422069-81efc85e0a79?w=400&h=300&fit=crop&q=80',
  'french-bulldog':
    'https://images.unsplash.com/photo-1561754050-9a1ee0470c73?w=400&h=300&fit=crop&q=80',
  'border-collie':
    'https://images.unsplash.com/photo-1675726377621-abe40eec0286?w=400&h=300&fit=crop&q=80',
  boxer:
    'https://images.unsplash.com/photo-1619876451741-407e8350442e?w=400&h=300&fit=crop&q=80',
  beagle:
    'https://images.unsplash.com/photo-1631048905843-88f82fba8fd4?w=400&h=300&fit=crop&q=80',
  poodle:
    'https://images.unsplash.com/photo-1651273427958-bf78352e39be?w=400&h=300&fit=crop&q=80',
  'labrador-retriever':
    'https://images.unsplash.com/photo-1537204696486-967f1b7198c8?w=400&h=300&fit=crop&q=80',
  'german-shepherd':
    'https://images.unsplash.com/photo-1605725657590-b2cf0d31b1a5?w=400&h=300&fit=crop&q=80',
  husky:
    'https://images.unsplash.com/photo-1590419690008-905895e8fe0d?w=400&h=300&fit=crop&q=80',
  'siberian-husky':
    'https://images.unsplash.com/photo-1590419690008-905895e8fe0d?w=400&h=300&fit=crop&q=80',
  'bull-terrier':
    'https://images.unsplash.com/photo-1729177753935-aa2d16074a4b?w=400&h=300&fit=crop&q=80',
  'cairn-terrier':
    'https://images.unsplash.com/photo-1762968755013-7e25a0bccc1d?w=400&h=300&fit=crop&q=80',
  'cavalier-king-charles-spaniel':
    'https://images.unsplash.com/photo-1613210609371-455e3aa5fb8c?w=400&h=300&fit=crop&q=80',
  'cocker-spaniel':
    'https://images.unsplash.com/photo-1657088746570-0218626e8f55?w=400&h=300&fit=crop&q=80',
  'corgi-pembroke':
    'https://images.unsplash.com/photo-1653760188729-894d6518581e?w=400&h=300&fit=crop&q=80',
  'english-springer-spaniel':
    'https://images.unsplash.com/photo-1678809325154-d9484eb736be?w=400&h=300&fit=crop&q=80',
  havanese:
    'https://images.unsplash.com/photo-1651161844904-8c651256ea73?w=400&h=300&fit=crop&q=80',
  'jack-russell-terrier':
    'https://images.unsplash.com/photo-1532275522382-fc0742e109d9?w=400&h=300&fit=crop&q=80',
  'miniature-schnauzer':
    'https://images.unsplash.com/photo-1580467277788-c6e040296602?w=400&h=300&fit=crop&q=80',
  newfoundland:
    'https://images.unsplash.com/photo-1632674764208-bb420a102ace?w=400&h=300&fit=crop&q=80',
  papillon:
    'https://images.unsplash.com/photo-1714846624589-bae6531e86e6?w=400&h=300&fit=crop&q=80',
  pomeranian:
    'https://images.unsplash.com/photo-1687702563085-2f550461f24c?w=400&h=300&fit=crop&q=80',
  'saint-bernard':
    'https://images.unsplash.com/photo-1637636069267-699b447ed58b?w=400&h=300&fit=crop&q=80',
  'shetland-sheepdog':
    'https://images.unsplash.com/photo-1680513971386-2563db81fe37?w=400&h=300&fit=crop&q=80',
  'yorkshire-terrier':
    'https://images.unsplash.com/photo-1689009480504-6420452a7e8e?w=400&h=300&fit=crop&q=80',

  // ===== MIXED/DESIGNER DOGS =====
  aussiedoodle:
    'https://images.unsplash.com/photo-1648315131548-bffab70e999b?w=400&h=300&fit=crop&q=80',
  bernedoodle:
    'https://images.unsplash.com/photo-1704227170709-5e5ddefcb4f7?w=400&h=300&fit=crop&q=80',
  cavapoo:
    'https://images.unsplash.com/photo-1610961039566-f695fe01830a?w=400&h=300&fit=crop&q=80',
  chiweenie:
    'https://images.unsplash.com/photo-1628524783675-275f685b3ad6?w=400&h=300&fit=crop&q=80',
  cockapoo:
    'https://images.unsplash.com/photo-1693496938332-deb5f37d9179?w=400&h=300&fit=crop&q=80',
  goldendoodle:
    'https://images.unsplash.com/photo-1575143490241-00d2b11a6335?w=400&h=300&fit=crop&q=80',
  labradoodle:
    'https://images.unsplash.com/photo-1607420376568-7751ff7971d2?w=400&h=300&fit=crop&q=80',
  maltipoo:
    'https://images.unsplash.com/photo-1713575189427-3d5ec99132cc?w=400&h=300&fit=crop&q=80',
  morkie:
    'https://images.unsplash.com/photo-1755210578266-88b96a6de3c3?w=400&h=300&fit=crop&q=80',
  pomsky:
    'https://images.unsplash.com/photo-1674242651608-583dd17c29bd?w=400&h=300&fit=crop&q=80',
  puggle:
    'https://images.unsplash.com/photo-1646753819552-70b3a1247f28?w=400&h=300&fit=crop&q=80',
  schnoodle:
    'https://images.unsplash.com/photo-1618520460311-31386d2c5093?w=400&h=300&fit=crop&q=80',
  sheepadoodle:
    'https://images.unsplash.com/photo-1618879926002-5b8c0cb4fce5?w=400&h=300&fit=crop&q=80',
  'shih-poo':
    'https://images.unsplash.com/photo-1624065935863-bf91d00e2c76?w=400&h=300&fit=crop&q=80',
  yorkipoo:
    'https://images.unsplash.com/photo-1612804829525-b6d7472df15e?w=400&h=300&fit=crop&q=80',

  // ===== CATS =====
  'persian-cat':
    'https://images.unsplash.com/photo-1585137173132-cf49e10ad27d?w=400&h=300&fit=crop&q=80',
  'maine-coon':
    'https://images.unsplash.com/photo-1606213651356-0272cc0becd2?w=400&h=300&fit=crop&q=80',
  'siamese-cat':
    'https://images.unsplash.com/photo-1568309386325-ef86f13ac533?w=400&h=300&fit=crop&q=80',
  'british-shorthair':
    'https://images.unsplash.com/photo-1629624467541-f73ef8f12df2?w=400&h=300&fit=crop&q=80',
  'bengal-cat':
    'https://images.unsplash.com/photo-1603277160434-df7471138363?w=400&h=300&fit=crop&q=80',
  bengal:
    'https://images.unsplash.com/photo-1603277160434-df7471138363?w=400&h=300&fit=crop&q=80',
  'ragdoll-cat':
    'https://images.unsplash.com/photo-1620933288385-b2f6f1931d9e?w=400&h=300&fit=crop&q=80',
  ragdoll:
    'https://images.unsplash.com/photo-1620933288385-b2f6f1931d9e?w=400&h=300&fit=crop&q=80',
  abyssinian:
    'https://images.unsplash.com/photo-1598005532299-f48db04a2637?w=400&h=300&fit=crop&q=80',
  birman:
    'https://images.unsplash.com/photo-1632041068365-215243fd40f0?w=400&h=300&fit=crop&q=80',
  burmese:
    'https://images.unsplash.com/photo-1518529303982-333bdcaf96e2?w=400&h=300&fit=crop&q=80',
  'devon-rex':
    'https://images.unsplash.com/photo-1701007816120-41c4f908221b?w=400&h=300&fit=crop&q=80',
  'exotic-shorthair':
    'https://images.unsplash.com/photo-1581460000963-755eec994cf0?w=400&h=300&fit=crop&q=80',
  himalayan:
    'https://images.unsplash.com/photo-1704122634191-4d3e5429f374?w=400&h=300&fit=crop&q=80',
  'russian-blue':
    'https://images.unsplash.com/photo-1602268867508-b058cb9c3e99?w=400&h=300&fit=crop&q=80',
  'scottish-fold':
    'https://images.unsplash.com/photo-1627373574917-572a86881d73?w=400&h=300&fit=crop&q=80',
  sphynx:
    'https://images.unsplash.com/photo-1547565295-182fb8657b6b?w=400&h=300&fit=crop&q=80',
  tonkinese:
    'https://images.unsplash.com/photo-1596636525749-494ff8b9f3a7?w=400&h=300&fit=crop&q=80',

  // ===== RABBITS & SMALL MAMMALS =====
  'holland-lop':
    'https://images.unsplash.com/photo-1707835774707-2861ff96de3e?w=400&h=300&fit=crop&q=80',
  'syrian-hamster':
    'https://images.unsplash.com/photo-1592159371936-61a70cbeb5f7?w=400&h=300&fit=crop&q=80',
  'american-fuzzy-lop':
    'https://images.unsplash.com/photo-1657973623144-a691536d5491?w=400&h=300&fit=crop&q=80',
  'angora-rabbit':
    'https://images.unsplash.com/photo-1643010790410-191ffa19f58e?w=400&h=300&fit=crop&q=80',
  'californian-rabbit':
    'https://images.unsplash.com/photo-1659396795763-f056d08aa254?w=400&h=300&fit=crop&q=80',
  'dutch-rabbit':
    'https://images.unsplash.com/photo-1606210869061-c0e9558c76a3?w=400&h=300&fit=crop&q=80',
  'flemish-giant':
    'https://images.unsplash.com/photo-1753636783593-47ea15a5941e?w=400&h=300&fit=crop&q=80',
  'lionhead-rabbit':
    'https://images.unsplash.com/photo-1654055445527-744fcb989ee0?w=400&h=300&fit=crop&q=80',
  'mini-rex':
    'https://images.unsplash.com/photo-1711014757456-d8c052723d93?w=400&h=300&fit=crop&q=80',
  'netherland-dwarf':
    'https://images.unsplash.com/photo-1570989086575-503363bb091b?w=400&h=300&fit=crop&q=80',
  'new-zealand-rabbit':
    'https://images.unsplash.com/photo-1573170447639-dab083dfc221?w=400&h=300&fit=crop&q=80',
  'polish-rabbit':
    'https://images.unsplash.com/photo-1739643678074-bdc60980995b?w=400&h=300&fit=crop&q=80',
  'rex-rabbit':
    'https://images.unsplash.com/photo-1711014757456-d8c052723d93?w=400&h=300&fit=crop&q=80',
  'campbell-dwarf-hamster':
    'https://images.unsplash.com/photo-1428394808097-a9c2da33c92c?w=400&h=300&fit=crop&q=80',
  'chinese-hamster':
    'https://images.unsplash.com/photo-1557996971-f245264c98b9?w=400&h=300&fit=crop&q=80',
  'dwarf-winter-white-hamster':
    'https://images.unsplash.com/photo-1656429377631-6cbf13a698f8?w=400&h=300&fit=crop&q=80',
  'roborovski-hamster':
    'https://images.unsplash.com/photo-1676918555344-e8e8f9ba410d?w=400&h=300&fit=crop&q=80',

  // ===== BIRDS =====
  cockatiel:
    'https://images.unsplash.com/photo-1648398476212-6a5a77353e32?w=400&h=300&fit=crop&q=80',
  'african-grey-parrot':
    'https://images.unsplash.com/photo-1626133830160-2d463a6b64a7?w=400&h=300&fit=crop&q=80',
  budgerigar:
    'https://images.unsplash.com/photo-1594911588637-c9e891bd7858?w=400&h=300&fit=crop&q=80',
  canary:
    'https://images.unsplash.com/photo-1654181920354-5c4add3989a1?w=400&h=300&fit=crop&q=80',
  cockatoo:
    'https://images.unsplash.com/photo-1664390748752-8e3e6ea4643e?w=400&h=300&fit=crop&q=80',
  conure:
    'https://images.unsplash.com/photo-1628496920588-ddcbc5ddf51b?w=400&h=300&fit=crop&q=80',
  finch:
    'https://images.unsplash.com/photo-1699622393243-daa26767b857?w=400&h=300&fit=crop&q=80',
  lovebird:
    'https://images.unsplash.com/photo-1634476824814-c1c014f51a71?w=400&h=300&fit=crop&q=80',
  macaw:
    'https://images.unsplash.com/photo-1667842287369-dfe75b66b195?w=400&h=300&fit=crop&q=80',
  parakeet:
    'https://images.unsplash.com/photo-1594911588637-c9e891bd7858?w=400&h=300&fit=crop&q=80',
  parrotlet:
    'https://images.unsplash.com/photo-1762987015809-914a897785cb?w=400&h=300&fit=crop&q=80',

  // ===== REPTILES =====
  'bearded-dragon':
    'https://images.unsplash.com/photo-1619816128374-a6b4766ca92c?w=400&h=300&fit=crop&q=80',
  'leopard-gecko':
    'https://images.unsplash.com/photo-1633767319476-d43c16b828c4?w=400&h=300&fit=crop&q=80',
  'ball-python':
    'https://images.unsplash.com/photo-1651093161533-76e8cfedb83b?w=400&h=300&fit=crop&q=80',
  'blue-tongue-skink':
    'https://images.unsplash.com/photo-1666255791374-ca9c4c1fb09e?w=400&h=300&fit=crop&q=80',
  'corn-snake':
    'https://images.unsplash.com/photo-1611245241464-97cf73d233a1?w=400&h=300&fit=crop&q=80',
  'crested-gecko':
    'https://images.unsplash.com/photo-1553722665-ed16a69677d6?w=400&h=300&fit=crop&q=80',
  'green-iguana':
    'https://images.unsplash.com/photo-1610604695612-ae85d1a6e54e?w=400&h=300&fit=crop&q=80',
  'red-eared-slider':
    'https://images.unsplash.com/photo-1684105556265-0b53170afc79?w=400&h=300&fit=crop&q=80',

  // ===== LIVESTOCK =====
  alpaca:
    'https://images.unsplash.com/photo-1695824939028-ef99c3cd4abf?w=400&h=300&fit=crop&q=80',
  cattle:
    'https://images.unsplash.com/photo-1666642478956-089fbb28254b?w=400&h=300&fit=crop&q=80',
  chicken:
    'https://images.unsplash.com/photo-1630090374791-c9eb7bab3935?w=400&h=300&fit=crop&q=80',
  donkey:
    'https://images.unsplash.com/photo-1652533114972-9769c2f2a391?w=400&h=300&fit=crop&q=80',
  goat: 'https://images.unsplash.com/photo-1593750187970-84858a2aaf5e?w=400&h=300&fit=crop&q=80',
  horse:
    'https://images.unsplash.com/photo-1625515400802-f9e5b64e0877?w=400&h=300&fit=crop&q=80',
  'miniature-horse':
    'https://images.unsplash.com/photo-1696182740362-69377af4f94c?w=400&h=300&fit=crop&q=80',
  pig: 'https://images.unsplash.com/photo-1616109259043-fd30a7663a5d?w=400&h=300&fit=crop&q=80',
  sheep:
    'https://images.unsplash.com/photo-1614183654058-0bac05af5ab0?w=400&h=300&fit=crop&q=80',
};

const petTypeFallbacks: Record<string, string> = {
  dog: 'https://images.unsplash.com/photo-1601758228041-4e1e3b5e8f8e?w=400&h=300&fit=crop&q=80',
  cat: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=400&h=300&fit=crop&q=80',
  rabbit:
    'https://images.unsplash.com/photo-1535241749838-4c77f90a1f59?w=400&h=300&fit=crop&q=80',
  bird: 'https://images.unsplash.com/photo-1552728089-57bdde30bfe8?w=400&h=300&fit=crop&q=80',
  reptile:
    'https://images.unsplash.com/photo-1585318818458-a8be4c5e9b3f?w=400&h=300&fit=crop&q=80',
  livestock:
    'https://images.unsplash.com/photo-1560807707-8a0872f1f8dd?w=400&h=300&fit=crop&q=80',
};

export function getBreedImageUrl(
  breedId: string,
  _breedImage: string,
  petType: string,
  width: number = 400,
  height: number = 300
): string {
  const curatedUrl = breedImageMap[breedId];

  if (curatedUrl) {
    return curatedUrl.replace(/w=\d+&h=\d+/, `w=${width}&h=${height}`);
  }

  return getPetTypeFallbackUrl(petType, width, height);
}

export function getPetTypeFallbackUrl(
  petType: string,
  width: number = 400,
  height: number = 300
): string {
  const fallbackUrl = petTypeFallbacks[petType];

  if (fallbackUrl) {
    return fallbackUrl.replace(/w=\d+&h=\d+/, `w=${width}&h=${height}`);
  }

  return `https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=${width}&h=${height}&fit=crop&q=80`;
}

export function getCuratedBreedCount(): number {
  return Object.keys(breedImageMap).length;
}
